/**
 * @fileoverview DashboardHeader's signed-in user identity block and account dropdown.
 * @description Split out of DashboardHeader purely to keep that component's own render function
 * short — owns its own open/close state and the logout mutation (outside-click/Escape-to-close is
 * useOutsideClick), so DashboardHeader itself only needs to render this when `user` is present.
 * Holds four actions: Profile Details (opens the profile dialog via `onOpenProfile`, falling back
 * to navigating to the profile route when none is passed), Organisation Details (opens that dialog
 * via `onOpenOrganisation`, and only appears when the app passes one), Settings (plain navigation)
 * and Log out (destructive, visually separated with a top border and the shared error color). Icons
 * are static assets (src/assets/svg/account-menu/), matching this app's existing icon convention
 * (see LoginForm's/TwoFactorForm's mailIcon/lockIcon) rather than inline SVG — their color is
 * baked into the file itself since none of them need to dynamically recolor.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useCallback, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import chevronDownIcon from '@/assets/svg/account-menu/icon-chevron-down.svg'
import logoutIcon from '@/assets/svg/account-menu/icon-logout.svg'
import organisationIcon from '@/assets/svg/account-menu/icon-organisation.svg'
import profileIcon from '@/assets/svg/account-menu/icon-profile.svg'
import settingsIcon from '@/assets/svg/account-menu/icon-settings-gear.svg'
import { ROUTES } from '@/shared/constants/routes.constants'
import { useOutsideClick } from '@/shared/hooks/useOutsideClick'
import { useLogout } from '@/shared/lib/auth/useLogout'
import { initialsFromEmail } from '@/shared/lib/auth/user-display.utils'

import type { AuthUser } from '@/shared/lib/auth/auth.types'

interface DashboardHeaderUserMenuProps {
  user: AuthUser
  onOpenProfile?: () => void
  onOpenOrganisation?: () => void
}

interface UserIdentityProps {
  user: AuthUser
}

/**
 * @description Renders the email/role text block shown in the dropdown's own header (the trigger
 * itself is a compact avatar + chevron capsule).
 * @param {UserIdentityProps} props - The signed-in user.
 * @param {AuthUser} props.user - Whose email/role to display.
 */
function UserIdentity({ user }: UserIdentityProps) {
  return (
    <>
      <p className="truncate text-compact-lg font-semibold text-ink">{user.email}</p>
      <p className="truncate text-xs text-slate">{user.activeRoleName}</p>
    </>
  )
}

interface MenuLinkItemProps {
  icon: ReactNode
  label: string
  onClick: () => void
  testId: string
}

/**
 * @description Renders one plain-navigation menu item (Profile Details, Settings) — the
 * destructive Log out action is styled separately, directly in DashboardHeaderUserMenu.
 * @param {MenuLinkItemProps} props - The item's icon, label, and click handler.
 * @param {ReactNode} props.icon - The item's leading icon.
 * @param {string} props.label - The item's visible label.
 * @param {() => void} props.onClick - Called when the item is clicked.
 * @param {string} props.testId - QA locator for the item.
 */
function MenuLinkItem({ icon, label, onClick, testId }: MenuLinkItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      onClick={onClick}
      className="flex w-full cursor-pointer items-center gap-2.5 px-3.5 py-2.5 text-left text-compact-lg font-medium text-ink hover:bg-input-bg"
    >
      {icon}
      {label}
    </button>
  )
}

interface MenuTriggerProps {
  email: string
  open: boolean
  onToggle: () => void
}

/**
 * @component
 * @param {MenuTriggerProps} props - The trigger's identity and state.
 * @param {string} props.email - The user's email: the button's accessible name and avatar initials.
 * @param {boolean} props.open - Whether the dropdown is open (rotates the chevron).
 * @param {() => void} props.onToggle - Opens or closes the dropdown.
 */
function MenuTrigger({ email, open, onToggle }: MenuTriggerProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-haspopup="menu"
      aria-label={email}
      data-testid="account-menu-button"
      className="flex cursor-pointer items-center gap-1.5 rounded-full border border-divider p-1 pr-2.5 hover:bg-input-bg"
    >
      <div className="flex h-[30px] w-[30px] items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
        {initialsFromEmail(email)}
      </div>
      <img
        src={chevronDownIcon}
        alt=""
        className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
      />
    </button>
  )
}

interface MenuLogoutItemProps {
  disabled: boolean
  onClick: () => void
}

/**
 * @component
 * @param {MenuLogoutItemProps} props - The Log out item's state.
 * @param {boolean} props.disabled - True while a logout request is in flight.
 * @param {() => void} props.onClick - Signs the user out.
 */
function MenuLogoutItem({ disabled, onClick }: MenuLogoutItemProps) {
  const { t } = useTranslation()

  return (
    <button
      type="button"
      role="menuitem"
      data-testid="account-menu-logout-item"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full cursor-pointer items-center gap-2.5 border-t border-divider px-3.5 py-2.5 text-left text-compact-lg font-medium text-error hover:bg-input-bg disabled:cursor-not-allowed disabled:opacity-60"
    >
      <img src={logoutIcon} alt="" className="h-4 w-4 shrink-0" />
      {t('actions.logout')}
    </button>
  )
}

/**
 * @component
 * @param {DashboardHeaderUserMenuProps} props - The signed-in user.
 * @param {AuthUser} props.user - Shown as an avatar + chevron capsule trigger, with a dropdown
 * holding the user's email/role and the Profile Details/Settings/Log out actions.
 * @param {() => void} [props.onOpenProfile] - Opens the profile dialog; when omitted, Profile
 * Details navigates to the profile route instead.
 * @param {() => void} [props.onOpenOrganisation] - Opens the organisation details dialog; when
 * omitted, the Organisation Details item does not show.
 */
function DashboardHeaderUserMenu({
  user,
  onOpenProfile,
  onOpenOrganisation,
}: DashboardHeaderUserMenuProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const logoutMutation = useLogout()
  const [menuOpen, setMenuOpen] = useState(false)
  const closeMenu = useCallback(() => setMenuOpen(false), [])
  const menuRef = useOutsideClick<HTMLDivElement>(menuOpen, closeMenu)

  /**
   * @description Closes the dropdown and navigates to the given route.
   * @param {string} route - The route to navigate to.
   */
  const goTo = (route: string) => {
    closeMenu()
    void navigate(route)
  }

  /**
   * @description Opens or closes the dropdown from its avatar button.
   */
  const toggleMenu = () => setMenuOpen((open) => !open)

  /**
   * @description Opens the profile dialog, or navigates to the profile route when the app gave no
   * dialog handler.
   */
  const openProfile = () => {
    if (onOpenProfile) {
      closeMenu()
      onOpenProfile()
      return
    }
    goTo(ROUTES.PROFILE)
  }

  /**
   * @description Navigates to Settings.
   */
  const openSettings = () => goTo(ROUTES.SETTINGS)

  /**
   * @description Signs the user out.
   */
  const signOut = () => logoutMutation.mutate()

  /**
   * @description Closes the dropdown and opens the organisation details dialog.
   */
  const openOrganisation = () => {
    closeMenu()
    onOpenOrganisation?.()
  }

  return (
    <div className="relative shrink-0" ref={menuRef}>
      <MenuTrigger email={user.email} open={menuOpen} onToggle={toggleMenu} />

      {menuOpen && (
        <div
          role="menu"
          data-testid="account-menu"
          className="absolute right-0 top-[calc(100%+8px)] z-20 w-56 rounded-lg border border-divider bg-white py-1.5 shadow-[0_14px_36px_rgba(11,26,52,0.18)]"
        >
          <div className="border-b border-divider px-3.5 py-2.5">
            <UserIdentity user={user} />
          </div>

          <MenuLinkItem
            label={t('actions.profileDetails')}
            onClick={openProfile}
            testId="account-menu-profile-item"
            icon={<img src={profileIcon} alt="" className="h-4 w-4 shrink-0" />}
          />
          {onOpenOrganisation && (
            <MenuLinkItem
              label={t('actions.organisationDetails')}
              onClick={openOrganisation}
              testId="account-menu-organisation-item"
              icon={<img src={organisationIcon} alt="" className="h-4 w-4 shrink-0" />}
            />
          )}
          <MenuLinkItem
            label={t('actions.settings')}
            onClick={openSettings}
            testId="account-menu-settings-item"
            icon={<img src={settingsIcon} alt="" className="h-4 w-4 shrink-0" />}
          />

          <MenuLogoutItem disabled={logoutMutation.isPending} onClick={signOut} />
        </div>
      )}
    </div>
  )
}

export default DashboardHeaderUserMenu
